import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiClock,FiShield,FiUsers,FiLock,FiEye,FiCheckCircle}=FiIcons;

const InfoSection=()=> {
  const infoCards=[
    {icon: FiClock,title: "24/7 Monitoring",description: "Permanente Überwachung unserer Netzwerkinfrastruktur.",color: "from-blue-500 to-indigo-500"},
    {icon: FiShield,title: "Schnelle Response",description: "Gezielte Maßnahmen bei Sicherheitsvorfällen.",color: "from-emerald-500 to-teal-500"},
    {icon: FiLock,title: "Datenschutz",description: "Strikte Einhaltung der DSGVO und Vertraulichkeit.",color: "from-violet-500 to-purple-500"},
    {icon: FiUsers,title: "Security Team",description: "Erfahrene IT-Spezialisten analysieren jeden Fall.",color: "from-orange-500 to-red-500"}
  ];

  const steps=[
    {step: "01",title: "Meldung",description: "Erfassung der Vorfallsdaten über unser Formular."},
    {step: "02",title: "Analyse",description: "Technische Prüfung durch das Security Operations Team."},
    {step: "03",title: "Mitigation",description: "Einleitung von Gegenmaßnahmen und Blockierung."},
    {step: "04",title: "Abschluss",description: "Dokumentation und Feedback an den Melder."}
  ];

  return (
    <section className="py-20 px-6">
      <div className="container mx-auto max-w-6xl">
        {/* Info Cards */}
        <motion.div 
          initial={{opacity: 0,y: 50}}
          whileInView={{opacity: 1,y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.8}}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent mb-4">
              Warum Visio Wasacon?
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Professionelle IT-Sicherheit und zuverlässiges Abuse-Management
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {infoCards.map((card,index)=> (
              <motion.div 
                key={index}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.6,delay: index * 0.1}}
                whileHover={{y: -10,scale: 1.02}}
                className="bg-slate-900/50 backdrop-blur-md border border-blue-500/10 rounded-2xl p-6 text-center group"
              >
                <motion.div 
                  whileHover={{rotate: 360}}
                  transition={{duration: 0.6}}
                  className={`bg-gradient-to-r ${card.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}
                >
                  <SafeIcon icon={card.icon} className="w-8 h-8 text-white" />
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                  {card.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {card.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Process Steps */}
        <motion.div 
          initial={{opacity: 0,y: 50}}
          whileInView={{opacity: 1,y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.8}}
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-emerald-400 to-teal-500 bg-clip-text text-transparent mb-4">
              Security Prozess
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Strukturierte Bearbeitung von Sicherheitsvorfällen
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step,index)=> (
              <motion.div 
                key={index}
                initial={{opacity: 0,scale: 0.8}}
                whileInView={{opacity: 1,scale: 1}}
                viewport={{once: true}}
                transition={{duration: 0.6,delay: index * 0.2}}
                className="relative"
              >
                {/* Connection line */}
                {index < steps.length - 1 && (
                  <motion.div 
                    initial={{scaleX: 0}}
                    whileInView={{scaleX: 1}}
                    viewport={{once: true}}
                    transition={{duration: 0.8,delay: index * 0.2 + 0.5}}
                    className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 transform origin-left opacity-30"
                  />
                )}
                <div className="text-center">
                  <motion.div 
                    whileHover={{scale: 1.1}}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 relative shadow-lg shadow-blue-900/40"
                  >
                    <span className="text-white font-bold text-lg">{step.step}</span>
                  </motion.div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Additional Information Section */}
        <motion.div 
          initial={{opacity: 0,y: 50}}
          whileInView={{opacity: 1,y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.8,delay: 0.3}}
          className="mt-20"
        >
          <div className="bg-slate-900/50 backdrop-blur-md border border-blue-500/10 rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent mb-8 text-center">
              Richtlinien
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white">Meldefähige Vorfälle</h3>
                <ul className="space-y-2 text-gray-400">
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>Spam und unerwünschte Werbung</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>Malware-Hostings / Distribution</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>Netzwerkangriffe (DDoS, Portscans)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>Urheberrechtsverletzungen</span>
                  </li>
                </ul>
              </div>
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white">Benötigte Daten</h3>
                <ul className="space-y-2 text-gray-400">
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiEye} className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Zeitstempel (UTC)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiEye} className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Quell-IP und Ziel-IP</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <SafeIcon icon={FiEye} className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Log-Auszüge (Plaintext)</span>
                  </li>
                </ul>
                <div className="mt-8 p-6 bg-blue-900/10 border border-blue-500/20 rounded-2xl">
                  <h4 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-blue-400" />
                    <span>SLA & Bearbeitung</span>
                  </h4>
                  <ul className="space-y-2 text-gray-400 text-sm">
                    <li>• Automatisierte Eingangsbestätigung</li>
                    <li>• Manuelle Prüfung kritischer Fälle</li>
                    <li>• Abuse-Postfach: abuse@wasacon.net</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InfoSection;